/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import EmberObject, { action, computed } from '@ember/object';
import { TrackedArray } from 'tracked-built-ins';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { enumOption } from '@linc-technologies/linced-common/helpers/enum-option';
import GradingMethod from 'linced-app/enums/grading-method';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { classNames } from '@ember-decorators/component';

@classNames('flex', 'layout-row')
export default class GradeSchedulePicker extends Component {
  // Services
  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  GradingMethod = GradingMethod;
  Scopes = Scopes;

  // Args
  // dialogOpen specifies whether the grade schedule editor is open.
  @tracked
  dialogOpen = false;

  // editingSchedule provides a schedule to be edited.
  @tracked
  editingSchedule = null;

  // scheduleDialogOpen specifies whether the grade schedule delete dialog is open.
  @tracked
  scheduleDialogOpen = false;

  // schedule provides a schedule to be deleted.
  schedule = null;

  // onChange provides a callback that will be called when the grade schedule
  // is changed.
  onChange() {}

  // onClose provides a callback that is called when a dialog closes. The
  // caller uses it to set its dialogOpen and scheduleDialogOpen to false.
  onClose() {}

  // schedules provides an array of grade schedules.
  schedules = null;

  // selected provides the currently selected grade schedule.
  selected = null;

  // The form to register the rendered field with, so it counts towards the
  // form's validity.
  parentComponent = null;

  // State
  @tracked
  saveInProgress = false;

  @tracked
  errorMessage = '';

  @tracked
  label = '';

  @action
  setLabel(value) {
    this.label = value;
  }

  @tracked
  description = '';

  @action
  setDescription(value) {
    this.description = value;
  }

  @tracked
  isShared = true;

  @action
  setIsShared(value) {
    this.isShared = value;
  }

  @tracked
  awardMethod = null;

  @action
  setAwardMethod(value) {
    this.awardMethod = value;
  }

  // awardMethod = GradeMethod.NUMERIC
  @tracked
  gradeFrom = 0;

  @action
  setGradeFrom(value) {
    this.gradeFrom = value;
  }

  @tracked
  gradeTo = 0;

  @action
  setGradeTo(value) {
    this.gradeTo = value;
  }

  @tracked
  gradeStep = 0;

  @action
  setGradeStep(value) {
    this.gradeStep = value;
  }

  // awardMethod = GradeMethod.SELECT || GradeMethod.MULTIPLE_SELECT || GradeMethod.ICON_SELECT
  // TrackedArray, not a plain array: bound directly to <DragSortList
  // @items={{this.selectOptions}}>, whose moveOption dragEndAction mutates
  // it in place (splice) - needs to stay reactive without reassigning the
  // whole array on every drag.
  @tracked
  selectOptions = new TrackedArray([{}]);

  // assessments returns an array of assessments made up of the linkedAssessment & linkedModules
  // so the user can select the assessments the grade schedule is linked to
  @computed('linkedModules.[]', 'linkedAssessments.[]')
  get assessments() {
    let assessments = EmberObject.create({});

    if (!isEmpty(this.linkedModules)) {
      this.linkedModules.forEach((module) => {
        assessments[module.get('assessment.id')] = module.get('assessment');
      });
    }
    if (!isEmpty(this.linkedAssessments)) {
      this.linkedAssessments.forEach((assessment) => {
        assessments[assessment.get('id')] = assessment;
      });
    }
    return assessments;
  }

  @action
  openDialog() {
    this.resetForm();
    this.dialogOpen = true;
  }

  @action
  closeDialog() {
    this.dialogOpen = false;
    this.onClose();
  }

  @action
  closeScheduleDialog() {
    this.scheduleDialogOpen = false;
    this.onClose();
  }

  // closeScheduleDialogFor closes the schedule dialog only if it is still
  // open for schedule. The user can close the dialog and open a different
  // dialog before the request completes.
  closeScheduleDialogFor(schedule) {
    if (this.scheduleDialogOpen && this.schedule === schedule) {
      this.closeScheduleDialog();
    }
  }

  @action
  openAssessment(assessment) {
    this.router.transitionTo('admin.assessment', assessment.get('id'));
  }

  @action
  gradeScheduleAction(actionType) {
    let schedule = this.schedule;
    switch (actionType) {
      case 'Delete':
        schedule
          .destroyRecord()
          .then(() => {
            this.closeScheduleDialogFor(schedule);
            this.toaster.success('Successfully deleted Grade Schedule!');
          })
          .catch(() => {
            this.toaster.error(
              'Error deleting Grade Schedule, please try again.',
            );
          });
        break;
      case 'Unhide':
      case 'Hide':
        schedule.toggleProperty('isHidden');
        schedule
          .save()
          .then(() => {
            this.closeScheduleDialogFor(schedule);
            this.toaster.success(`Successfully updated Grade Schedule!`);
          })
          .catch(() => {
            this.toaster.error(
              'Error updating Grade Schedule, please try again.',
            );
          });
        break;
    }
  }

  @action
  save() {
    let editingSchedule = this.editingSchedule;
    let schedule = editingSchedule;
    let store = this.store;
    let isNew = false;
    this.saveInProgress = true;

    // Create a new Grade Schedule if needed
    if (isEmpty(schedule)) {
      isNew = true;
      schedule = store.createRecord('assessments/grade-schedule', {
        schoolId: this.get('session.school'),
        authorId: this.get('session.person'),
      });
    }

    // Set properties from form
    let data = this.getProperties('label', 'description', 'isShared');
    let awardMethod = this.get('awardMethod.value');
    switch (awardMethod) {
      case GradingMethod.NUMERIC:
        Object.assign(data, {
          awardMethod,
          numeric: {
            range: {
              start: this.gradeFrom,
              end: this.gradeTo,
            },
            defaultNumericStepping: this.gradeStep,
          },
        });
        break;
      case GradingMethod.SELECT:
      case GradingMethod.MULTIPLE_SELECT:
      case GradingMethod.ICON_SELECT:
        Object.assign(data, {
          awardMethod,
          select: {
            // Send a plain array, not the TrackedArray proxy itself, into
            // the save payload/model attribute.
            values: [...this.selectOptions],
          },
        });
        break;
    }

    schedule.setProperties(data);

    schedule
      .save()
      .then(() => {
        // Close the editor only if it is still open for this schedule.
        if (this.dialogOpen && this.editingSchedule === editingSchedule) {
          this.closeDialog();
        }
        if (isNew && this.onChange) {
          // Select the newly-created schedule.
          this.onChange(schedule);
        }
      })
      .catch((err) => {
        this.errorMessage = err;
      })
      .then(() => {
        this.saveInProgress = false;
      });
  }

  @action
  moveOption({
    draggedItem,
    sourceList,
    sourceIndex,
    targetList,
    targetIndex,
  }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    // ember-drag-sort already adjusts targetIndex to account for the
    // remove-then-insert shift when sourceList === targetList (reordering
    // within the same list), so a plain splice-then-splice in this exact
    // order matches the old removeAt/insertAt semantics.
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, draggedItem);
  }

  @action
  addOption() {
    this.selectOptions.push({ isAddedOption: true });
  }

  @action
  removeOption(option) {
    let pos = this.selectOptions.indexOf(option);
    if (pos > -1) {
      this.selectOptions.splice(pos, 1);
    }
  }

  resetForm() {
    this.awardMethod = enumOption([GradingMethod, GradingMethod.NUMERIC]);
    this.description = '';
    this.errorMessage = '';
    this.gradeFrom = 0;
    this.editingSchedule = null;
    this.gradeStep = 1;
    this.gradeTo = 100;
    this.isShared = true;
    this.label = '';
    this.selectOptions = new TrackedArray([{ isAddedOption: true }]);
  }

  setForm(schedule = null) {
    if (isEmpty(schedule)) {
      this.resetForm();
      return;
    }

    let awardMethod = schedule.get('awardMethod');
    this.editingSchedule = schedule;
    this.label = schedule.get('label');
    this.description = schedule.get('description');
    this.isShared = schedule.get('isShared');
    this.awardMethod = enumOption([GradingMethod, awardMethod]);

    switch (awardMethod) {
      case GradingMethod.NUMERIC:
        this.gradeFrom = schedule.get('numeric.range.start');
        this.gradeTo = schedule.get('numeric.range.end');
        this.gradeStep = schedule.get('numeric.defaultNumericStepping');
        break;

      case GradingMethod.SELECT:
      case GradingMethod.MULTIPLE_SELECT:
      case GradingMethod.ICON_SELECT:
        this.selectOptions = new TrackedArray(schedule.get('select.values'));
        break;
    }
  }

  // Lifecycle Hooks
  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    let schedule = this.editingSchedule;
    if (this.dialogOpen && schedule) {
      // Dialog has been triggered with a schedule to edit; reset and populate the form.
      this.setForm(schedule);
    }
  }
}
